import { CheckCircle2, Circle, CircleDot, ClipboardCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useBrandThemeConfig } from '@/hooks/useBrandThemeConfig';
import { OpsChecklistCoach } from '@/components/checklist/OpsChecklistCoach';
import {
  buildOpsChecklistRuntime,
  resolveTripChecklistTemplate,
  type OpsChecklistRuntimeStep,
} from '@/lib/ops-checklist';

interface OpsChecklistPanelProps {
  tripType?: string | null;
  serviceMode?: string | null;
  currentStatus: string;
  compact?: boolean;
}

function StepIcon({ step }: { step: OpsChecklistRuntimeStep }) {
  if (step.state === 'done') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (step.state === 'next') return <CircleDot className="h-4 w-4 text-primary" />;
  return <Circle className="h-4 w-4 text-muted-foreground" />;
}

export function OpsChecklistPanel({ tripType, serviceMode, currentStatus, compact = false }: OpsChecklistPanelProps) {
  const { brandTheme } = useBrandThemeConfig();
  const template = resolveTripChecklistTemplate(tripType, serviceMode);
  const runtime = buildOpsChecklistRuntime(template, currentStatus);

  return (
    <Card className={compact ? 'border-blue-200/70 bg-blue-50/20' : undefined}>
      <CardHeader className={cn('pb-2', compact && 'px-3 py-2')}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            {template.label}
          </CardTitle>
          <Badge variant={runtime.percent === 100 ? 'success' : 'secondary'} className="text-[10px]">
            {runtime.completed}/{runtime.total}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{template.description}</p>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-300',
              runtime.percent === 100 ? 'bg-green-500' : 'bg-primary',
            )}
            style={{ width: `${runtime.percent}%` }}
          />
        </div>
        <OpsChecklistCoach
          assistantPersona={brandTheme.assistantPersona}
          gamificationMode={brandTheme.gamificationMode}
          palette={brandTheme.palette}
          completed={runtime.completed}
          total={runtime.total}
          percent={runtime.percent}
          nextStepLabel={runtime.nextStep?.label}
          contextLabel={template.contextLabel}
          compact
        />
      </CardHeader>
      <CardContent className={cn('space-y-1 pb-3', compact && 'px-3')}>
        {runtime.steps.map((step) => (
          <div
            key={step.id}
            className={cn(
              'grid grid-cols-[18px_1fr_auto] gap-2 rounded-md px-1.5 py-1.5 text-xs',
              step.state === 'next' && 'bg-primary/5',
            )}
          >
            <StepIcon step={step} />
            <div className="min-w-0">
              <p className={cn('font-medium leading-tight', step.state === 'done' && 'text-muted-foreground line-through')}>
                {step.label}
              </p>
              <p className="mt-0.5 leading-relaxed text-muted-foreground">{step.description}</p>
              {step.evidence && (
                <p className="mt-1 text-[10px] font-medium text-muted-foreground">
                  Evidence: {step.evidence}
                </p>
              )}
            </div>
            <span className="text-[10px] text-muted-foreground">
              {step.state === 'done' ? 'Done' : step.state === 'next' ? 'Next' : 'Open'}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
